/**
 * Compares two objects (e.g., query parameters) and returns a new object
 * containing the keys and values that differ between them.
 *
 * @param {Object} currentParams - The current query parameters.
 * @param {Object} newParams - The updated query parameters.
 * @returns {Array} - An array with keys that have different values or are missing.
 */
export const getQueryParamDifferences = (currentParams, newParams) => {
  const result = {};

  for (const [key, value] of Object.entries(newParams)) {
    if (currentParams[key] !== value) {
      result[key] = value;
    }
  }

  return Object.keys(result);
};

/**
 * Serializes a value into a string format.
 * If the value is an array, it joins elements with a comma.
 * For other types, it returns the value as-is.
 *
 * @param {any} value - The value to serialize.
 * @returns {string} - The serialized value.
 */
export const serializeValue = (value) => {
  if (Array.isArray(value)) {
    return value.join(',');
  }
  return value;
};

/**
 * Deserializes a string value into its original format.
 * If the value is a string containing commas, it splits it into an array.
 * For other types, it returns the value as-is.
 *
 * @param {string} value - The value to deserialize.
 * @returns {any} - The deserialized value.
 */
export const deserializeValue = (value) => {
  if (typeof value === 'string' && value.includes(',')) {
    return value.split(',');
  }
  return value;
};

/**
 * Transforms a single word by adding the prefix "sort"
 * and capitalizing the first letter of the word.
 *
 * @param {string} word - The word to transform.
 * @returns {string} - The transformed word with "sort" prefix.
 */
export const transformWord = (word) => {
  if (typeof word !== 'string') {
    throw new TypeError('Input must be a string.');
  }
  const capitalizedWord = word.charAt(0).toUpperCase() + word.slice(1);
  return `sort${capitalizedWord}`;
};

/**
 * Transforms an array of words by adding the prefix "sort"
 * and capitalizing the first letter of each word in the array.
 *
 * @param {string[]} words - The array of words to transform.
 * @returns {string[]} - The array of transformed words with "sort" prefix.
 */
export const transformWords = (words) => {
  if (!Array.isArray(words)) {
    throw new TypeError('Input must be an array of strings.');
  }
  return words.map((word) => transformWord(word));
};

/**
 * Escapes special RegExp characters in a string so it can be safely
 * used as a literal within a regular expression pattern.
 *
 * Escaped characters: . * + ? ^ $ { } ( ) | [ ] \
 *
 * @param {string} s - The input string to escape for RegExp usage.
 * @returns {string} - The escaped string safe for inclusion in RegExp patterns.
 */
export const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
